import { Button, Card, Col, Collapse, Form, Input, Row, Typography, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router-dom";
import HTTPClient from "../../utils/HTTPClient";
import numeral from "numeral";
import { CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

export default function CreateApp() {
    const { app_id } = useParams();
    const [form] = Form.useForm();
    const [app, setApp] = useState(null);
    const [resources, setResources] = useState(null);
    const [promoCode, setPromoCode] = useState("");
    const [isPromoCodeVerified, setIsPromoCodeVerified] = useState(false);
    const [originalPrice, setOriginalPrice] = useState(0);
    const [isChecking, setIsChecking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [price, setPrice] = useState(0);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });

    useEffect(() => {
        const fetch = async () => {
            HTTPClient.get("/app/_app_typologies")
                .then(async (res) => {
                    const present = res.data.find((a) => a.value === app_id);
                    if (!present) throw Error();
                    setApp(present);
                    form.setFieldValue("resources", present.resources[0]);
                    setResources(present.resources[0]);

                    setPrice(present.resources[0].price);
                    setOriginalPrice(present.resources[0].price);
                })
                .catch((e) => {
                    message.error(t("create_app.not_found"));
                    navigate("/pro/app");
                });
        };
        fetch();
    }, []);

    const handleSubmit = async (values) => {
        const body = {
            technology: app.value,
            resource_id: values.resources.value,
            name: values.name,
        };

        delete values.name;
        delete values.resources;
        body.extra_args = values;
        if (isPromoCodeVerified) {
            body.promo_code = promoCode;
        }
        setIsLoading(true);

        try {
            await HTTPClient.post("/stripe/create_app_checkout_session", body);
            message.success(t("create_app.success"));
            navigate("/pro/myapps");
        } catch (e) {
            // redirect to checkout portal
            if (e.response.status === 303) {
                window.location.replace(e.response.data);
                return;
            }
            const { error_code } = e?.response?.data;
            message.error(t([`errors.${error_code}`, "create_app.error"]));
        }
        setIsLoading(false);
    };

    const checkPromoCode = async () => {
        if (!promoCode) return;
        setIsChecking(true);
        try {
            const response = await HTTPClient.post("/stripe/check_promo_code", {
                name: promoCode,
                technology: app.value,
            });
            if (response.data?.discount) {
                const { discount, is_perc } = response.data;
                let tmp_price = price;

                if (is_perc) {
                    tmp_price = tmp_price - (tmp_price * discount) / 100;
                } else {
                    tmp_price = tmp_price - discount;
                }
                setIsPromoCodeVerified(true);
                setPrice(tmp_price);
            }
            message.success(t("create_app.promo_code_ok"));
        } catch (e) {
            const { error_code } = e?.response?.data;
            message.info(t([`errors.${error_code}`, "create_app.promo_code_ko"]));
        }
        setIsChecking(false);
    };

    const deletePromoCode = (priceToSet) => {
        setPrice(priceToSet);
        setOriginalPrice(priceToSet);
        setPromoCode("");
        setIsPromoCodeVerified(false);
    };

    const ref = useRef();

    if (!app) return null;
    const long_description = t(`apps.${app.value}.long_description`);

    return (
        <Card
            style={{ margin: "2em" }}
            cover={
                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "10px",
                    }}>
                    <div
                        style={{
                            width: "200px",
                        }}>
                        <img src={app.img} style={{ width: "100%", height: "auto" }} />
                    </div>
                </div>
            }>
            <Collapse
                size="small"
                bordered={false}
                defaultActiveKey={isLargeScreen ? ["1"] : []}
                items={[
                    {
                        key: "1",
                        label: t("create_app.more_info"),
                        children: (
                            <Text
                                style={{
                                    textAlign: isLargeScreen ? "center" : "justify",
                                    fontWeight: "300",
                                    display: "flex",
                                }}>
                                {long_description}
                            </Text>
                        ),
                    },
                ]}
            />

            <Form
                name="basic"
                ref={ref}
                form={form}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                onFinish={async (values) => {
                    await handleSubmit(values);
                }}
                validateTrigger="onSubmit"
                autoComplete="off">
                <Form.Item
                    label={t("create_app.resource")}
                    name="resources"
                    rules={[{ required: true, message: "Scegli le risorse!" }]}>
                    <Row style={{ width: "100%" }}>
                        {app.resources.map((data) => {
                            const description = t(`apps.${app.value}.resources.${data.value}`);
                            return (
                                <Col xs={24} sm={24} lg={8} xl={8} style={{ padding: "10px" }}>
                                    <Card
                                        onClick={() => {
                                            form.setFieldValue("resources", data);
                                            setResources(data);
                                            setPrice(data?.price);
                                            setOriginalPrice(data?.price);
                                            setPromoCode("");
                                            setIsPromoCodeVerified(false);
                                        }}
                                        style={{
                                            backgroundColor:
                                                resources?.value === data?.value
                                                    ? "#5f4dee"
                                                    : "#fafafa",
                                            color:
                                                resources?.value === data?.value
                                                    ? "#fafafa"
                                                    : "black",
                                            cursor: "pointer",
                                            textAlign: "center",
                                        }}>
                                        <b> {data?.label}</b>
                                        <br />
                                        {description}
                                        <br />€
                                        {numeral(data?.price / 100).format("0,0.00") + " al mese"}
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </Form.Item>
                <Form.Item
                    label={t("create_app.app_name")}
                    name="name"
                    rules={[
                        {
                            pattern: /^[a-z]{1}[a-z0-9\-]{2,18}[a-z0-9]{1}$/,
                            message: t("create_app.app_name_rule"),
                        },
                        { required: true, message: t("create_app.app_name_required") },
                    ]}>
                    <Input placeholder={t("create_app.app_name_placeholder")} />
                </Form.Item>
                {resources?.requirements?.map((requirement) => {
                    const label = t(`apps.${app.value}.${requirement?.value}.label`);
                    const tooltip = t(`apps.${app.value}.${requirement?.value}.tooltip`);

                    return (
                        <Form.Item
                            label={label}
                            name={requirement?.value}
                            tooltip={tooltip}
                            rules={[
                                {
                                    required: requirement?.required,
                                    message: t("create_app.required"),
                                },
                            ]}>
                            <Input
                                placeholder={`${t("create_app.please_insert")} ${label}`}
                                type={requirement?.type}
                                autoComplete="new-password"
                            />
                        </Form.Item>
                    );
                })}{" "}
                <Row
                    style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: price > 0 ? "space-between" : "flex-end",
                        flexDirection: isLargeScreen ? "row" : "column",
                        gap: "10px",
                    }}>
                    {price > 0 && (
                        <Collapse
                            style={{ width: isLargeScreen ? "50%" : "100%" }}
                            items={[
                                {
                                    key: "1",
                                    label: (
                                        <Row
                                            style={{
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "space-between",
                                            }}>
                                            <Typography.Text>
                                                {t("create_app.promo_code")}
                                            </Typography.Text>
                                            {isPromoCodeVerified && (
                                                <CheckOutlined style={{ color: "green" }} />
                                            )}
                                        </Row>
                                    ),

                                    children: (
                                        <>
                                            <Input
                                                placeholder={"APPBOX2024"}
                                                value={promoCode}
                                                onChange={(e) => setPromoCode(e.target.value)}
                                                disabled={isPromoCodeVerified}
                                            />
                                            {!isPromoCodeVerified && (
                                                <Button
                                                    style={{ marginTop: "10px", width: "100%" }}
                                                    type="primary"
                                                    onClick={() => checkPromoCode()}
                                                    size="large"
                                                    loading={isChecking}>
                                                    {t("create_app.verify")}
                                                </Button>
                                            )}

                                            {isPromoCodeVerified && (
                                                <Button
                                                    style={{ marginTop: "10px", width: "100%" }}
                                                    type="error"
                                                    onClick={() => deletePromoCode(originalPrice)}
                                                    size="small"
                                                    loading={isChecking}>
                                                    {t("create_app.delete")}
                                                </Button>
                                            )}
                                        </>
                                    ),
                                },
                            ]}
                        />
                    )}
                    <Button type="primary" htmlType="submit" size="large" loading={isLoading}>
                        {originalPrice == price ? (
                            <>
                                {t("create_app.buy")} €{" "}
                                <span
                                    style={{
                                        fontWeight: "300",
                                        fontSize: "large",
                                    }}>
                                    {numeral(price / 100).format("0,0.00")}
                                </span>{" "}
                                (1 {t("create_app.month")})
                            </>
                        ) : (
                            <>
                                {t("create_app.buy")}{" "}
                                <span
                                    style={{
                                        textDecoration: "line-through",
                                        fontWeight: "300",
                                        fontSize: "small",
                                    }}>
                                    {numeral(originalPrice / 100).format("0,0.00")}
                                </span>{" "}
                                <span
                                    style={{
                                        fontSize: "large",
                                    }}>
                                    €{numeral(price / 100).format("0,0.00")}
                                </span>{" "}
                                (1 {t("create_app.month")})
                            </>
                        )}
                    </Button>
                </Row>
            </Form>
        </Card>
    );
}
